import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useTimeline Hook - Manages Timeline Feature Database Operations
 * 
 * Handles:
 * - Loading user's timeline progress
 * - Tracking era unlocks
 * - Recording event completions
 * - Managing artifact collection
 * - Calculating unlock eligibility
 */
export const useTimeline = (user) => {
    const [progress, setProgress] = useState({});
    const [events, setEvents] = useState({});
    const [artifacts, setArtifacts] = useState({});
    const [achievements, setAchievements] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Initialize timeline for new users (unlock Era 1)
     */
    const initializeTimeline = useCallback(async () => {
        if (!user?.id) return;

        try {
            // Check if user already has timeline data
            const { data: existing } = await supabase
                .from('timeline_progress')
                .select('*')
                .eq('user_id', user.id)
                .eq('era_number', 1)
                .single();

            if (!existing) {
                // Initialize Era 1 as unlocked
                await supabase
                    .from('timeline_progress')
                    .insert({
                        user_id: user.id,
                        era_number: 1,
                        unlocked: true,
                        unlocked_at: new Date().toISOString(),
                        unlock_method: 'default',
                    });
            }
        } catch (err) {
            console.error('Error initializing timeline:', err);
        }
    }, [user?.id]);

    /**
     * Load all timeline data for the user
     */
    const loadTimelineData = useCallback(async () => {
        if (!user?.id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Load era progress
            const { data: progressData, error: progressError } = await supabase
                .from('timeline_progress')
                .select('*')
                .eq('user_id', user.id);

            if (progressError) throw progressError;

            // Convert array to object keyed by era_number
            const progressMap = {};
            (progressData || []).forEach(p => {
                progressMap[p.era_number] = p;
            });
            setProgress(progressMap);

            // Load event completions
            const { data: eventsData, error: eventsError } = await supabase
                .from('timeline_events')
                .select('*')
                .eq('user_id', user.id);

            if (eventsError) throw eventsError;

            // Convert array to object keyed by event_id
            const eventsMap = {};
            (eventsData || []).forEach(e => {
                eventsMap[e.event_id] = e;
            });
            setEvents(eventsMap);

            // Load artifacts
            const { data: artifactsData, error: artifactsError } = await supabase
                .from('timeline_artifacts')
                .select('*')
                .eq('user_id', user.id);

            if (artifactsError) throw artifactsError;

            // Convert array to object keyed by artifact_id
            const artifactsMap = {};
            (artifactsData || []).forEach(a => {
                artifactsMap[a.artifact_id] = a;
            });
            setArtifacts(artifactsMap);

            // Load achievements (from timeline_unlocks where type = 'achievement')
            const { data: achievementData, error: achievementError } = await supabase
                .from('timeline_unlocks')
                .select('*')
                .eq('user_id', user.id)
                .eq('unlock_type', 'achievement');

            if (achievementError) throw achievementError;

            // Convert array to object keyed by unlock_identifier (achievement ID)
            const achievementsMap = {};
            (achievementData || []).forEach(a => {
                achievementsMap[a.unlock_identifier] = a;
            });
            setAchievements(achievementsMap);

        } catch (err) {
            console.error('Error loading timeline data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    /**
     * Initialize and load data on mount
     */
    useEffect(() => {
        if (user?.id) {
            initializeTimeline().then(() => {
                loadTimelineData();
            });
        } else {
            setLoading(false);
        }
    }, [user?.id, initializeTimeline, loadTimelineData]);

    /**
     * Unlock an era
     */
    const unlockEra = useCallback(async (eraNumber, unlockMethod = 'manual') => {
        if (!user?.id) return false;

        try {
            const { error } = await supabase
                .from('timeline_progress')
                .upsert({
                    user_id: user.id,
                    era_number: eraNumber,
                    unlocked: true,
                    unlocked_at: new Date().toISOString(),
                    unlock_method: unlockMethod,
                }, {
                    onConflict: 'user_id,era_number'
                });

            if (error) throw error;

            // Record unlock in timeline_unlocks table
            await supabase
                .from('timeline_unlocks')
                .insert({
                    user_id: user.id,
                    unlock_type: 'era',
                    unlock_identifier: `era_${eraNumber}`,
                    unlock_method: unlockMethod,
                });

            // Reload data
            await loadTimelineData();
            return true;
        } catch (err) {
            console.error('Error unlocking era:', err);
            return false;
        }
    }, [user?.id, loadTimelineData]);

    /**
     * Complete an event
     */
    const completeEvent = useCallback(async (eventData) => {
        if (!user?.id) return false;

        const {
            eventId,
            eraNumber,
            accuracy,
            completionTimeMs,
            perfectCopy,
            hintsUsed = 0,
        } = eventData;

        try {
            // Get existing event data if any
            const existing = events[eventId];
            const isFirstCompletion = !existing?.completed;

            // Update or insert event completion
            const { error } = await supabase
                .from('timeline_events')
                .upsert({
                    user_id: user.id,
                    era_number: eraNumber,
                    event_id: eventId,
                    completed: true,
                    accuracy: accuracy,
                    completion_time_ms: completionTimeMs,
                    perfect_copy: perfectCopy,
                    attempts: (existing?.attempts || 0) + 1,
                    hints_used: hintsUsed,
                    first_completed_at: existing?.first_completed_at || new Date().toISOString(),
                    last_completed_at: new Date().toISOString(),
                    best_accuracy: Math.max(existing?.best_accuracy || 0, accuracy),
                    best_time_ms: existing?.best_time_ms
                        ? Math.min(existing.best_time_ms, completionTimeMs)
                        : completionTimeMs,
                }, {
                    onConflict: 'user_id,era_number,event_id'
                });

            if (error) throw error;

            // Also save to main activities table for admin visibility
            const durationSeconds = Math.round(completionTimeMs / 1000);
            const now = new Date();
            const startedAt = new Date(now.getTime() - completionTimeMs);

            // Calculate score based on accuracy (base 100 XP, scaled by accuracy)
            const baseScore = 100;
            const activityScore = Math.round(baseScore * (accuracy / 100));

            const { error: activityError } = await supabase
                .from('activities')
                .insert({
                    user_id: user.id,
                    mode: 'timeline',
                    input_mode: 'keyboard', // Timeline uses keyboard input for decoding
                    wpm: 20, // Default WPM for timeline (decode-focused, not speed-focused)
                    score: activityScore,
                    accuracy: Math.round(accuracy),
                    duration_seconds: durationSeconds,
                    started_at: startedAt.toISOString(),
                    ended_at: now.toISOString(),
                    character_stats: {
                        event_id: eventId,
                        era_number: eraNumber,
                        perfect_copy: perfectCopy,
                        hints_used: hintsUsed,
                        first_completion: isFirstCompletion,
                    }
                });

            if (activityError) {
                console.error('Error saving timeline activity:', activityError);
            }

            // Reload data (trigger will update era progress automatically)
            await loadTimelineData();

            return { success: true, firstCompletion: isFirstCompletion };
        } catch (err) {
            console.error('Error completing event:', err);
            return { success: false, error: err.message };
        }
    }, [user?.id, events, loadTimelineData]);

    /**
     * Collect an artifact
     */
    const collectArtifact = useCallback(async (artifactId, eraNumber, collectionMethod = 'event_completion', rarity = 'common') => {
        if (!user?.id) return false;

        try {
            // Check if already collected
            if (artifacts[artifactId]?.collected) {
                return { success: true, alreadyCollected: true };
            }

            const { error } = await supabase
                .from('timeline_artifacts')
                .upsert({
                    user_id: user.id,
                    artifact_id: artifactId,
                    era_number: eraNumber,
                    rarity: rarity,
                    collected: true,
                    collected_at: new Date().toISOString(),
                    collection_method: collectionMethod,
                }, {
                    onConflict: 'user_id,artifact_id'
                });

            if (error) throw error;

            // Record unlock
            await supabase
                .from('timeline_unlocks')
                .insert({
                    user_id: user.id,
                    unlock_type: 'artifact',
                    unlock_identifier: artifactId,
                    unlock_method: collectionMethod,
                });

            // Reload data
            await loadTimelineData();
            return { success: true, alreadyCollected: false };
        } catch (err) {
            console.error('Error collecting artifact:', err);
            return { success: false, error: err.message };
        }
    }, [user?.id, artifacts, loadTimelineData]);

    /**
     * Unlock an achievement
     */
    const unlockAchievement = useCallback(async (achievementId) => {
        if (!user?.id) return false;

        try {
            // Check if already unlocked
            if (achievements[achievementId]) {
                return { success: true, alreadyUnlocked: true };
            }

            const { error } = await supabase
                .from('timeline_unlocks')
                .insert({
                    user_id: user.id,
                    unlock_type: 'achievement',
                    unlock_identifier: achievementId,
                    unlock_method: 'event_reward',
                });

            if (error) throw error;

            // Reload data
            await loadTimelineData();
            return { success: true, newUnlock: true };
        } catch (err) {
            console.error('Error unlocking achievement:', err);
            return { success: false, error: err.message };
        }
    }, [user?.id, achievements, loadTimelineData]);

    /**
     * Complete era quiz
     */
    const completeQuiz = useCallback(async (eraNumber, score) => {
        if (!user?.id) return false;

        try {
            const { error } = await supabase
                .from('timeline_progress')
                .update({
                    quiz_completed: true,
                    quiz_score: score,
                    updated_at: new Date().toISOString(),
                })
                .eq('user_id', user.id)
                .eq('era_number', eraNumber);

            if (error) throw error;

            // Reload data
            await loadTimelineData();
            return true;
        } catch (err) {
            console.error('Error completing quiz:', err);
            return false;
        }
    }, [user?.id, loadTimelineData]);

    /**
     * Get statistics about timeline progress
     */
    const getStats = useCallback(() => {
        const totalEras = 8;
        const unlockedEras = Object.values(progress).filter(p => p.unlocked).length;
        const completedEvents = Object.values(events).filter(e => e.completed).length;
        const perfectCopies = Object.values(events).filter(e => e.perfect_copy).length;
        const collectedArtifacts = Object.values(artifacts).filter(a => a.collected).length;

        return {
            unlockedEras,
            totalEras,
            completedEvents,
            perfectCopies,
            collectedArtifacts,
            progressPercentage: Math.round((unlockedEras / totalEras) * 100),
        };
    }, [progress, events, artifacts]);

    return {
        // State
        progress,
        events,
        artifacts,
        achievements,
        loading,
        error,

        // Actions
        unlockEra,
        completeEvent,
        collectArtifact,
        unlockAchievement,
        completeQuiz,
        loadTimelineData,

        // Helpers
        getStats,
    };
};
